import { useEffect, useRef, useCallback } from 'react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}

function Modal({ isOpen, onClose, title, children }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return []
    return Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'))
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key !== 'Tab') return

      const focusableElements = getFocusableElements()
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (!firstElement || !lastElement) return

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    },
    [onClose, getFocusableElements]
  )

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'

      requestAnimationFrame(() => {
        const focusableElements = getFocusableElements()
        const firstElement = focusableElements[0]
        if (firstElement) {
          firstElement.focus()
        }
      })
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'

      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen, handleKeyDown, getFocusableElements])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-[var(--void-950)]/80 backdrop-blur-sm" />
      
      {/* Modal Panel */}
      <div
        ref={modalRef}
        className="glass-panel relative w-full max-w-xl max-h-[90vh] overflow-hidden animate-[float_0.3s_ease-out]"
        style={{ animationIterationCount: 1 }}
      >
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[var(--energy-cyan)] opacity-70" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[var(--energy-cyan)] opacity-70" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[var(--weave-400)] opacity-50" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[var(--weave-400)] opacity-50" />

        {/* Header */}
        <div className="relative px-6 py-4 border-b border-[var(--glass-border)]">
          {/* Top glow line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--energy-cyan)] to-transparent opacity-60" />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-[var(--energy-cyan)] rotate-45 shadow-[0_0_10px_var(--energy-cyan)]" />
              <h2 id="modal-title" className="text-lg font-semibold tracking-wider text-[var(--energy-glow)]">
                {title}
              </h2>
            </div>
            
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="relative w-8 h-8 flex items-center justify-center text-[var(--chrome-300)] hover:text-[var(--energy-cyan)] transition-colors group"
            >
              {/* Hover border */}
              <span className="absolute inset-0 border border-transparent group-hover:border-[var(--energy-cyan)] group-hover:rotate-45 transition-all duration-300 opacity-50" />
              <span className="text-xl leading-none">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {children}
        </div>

        {/* Bottom accent */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-[var(--weave-400)] to-transparent opacity-40" />
      </div>
    </div>
  )
}

export default Modal
